import { itemList, ItemListParams } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams, SourceTypeEnum } from "@/types/common";
import { OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import { Button, DatePicker, Dropdown, Flex, Input, notification, Select, Tooltip, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangeEnum, ConfirmedEnum } from "@/types/inventory";
import ExportExcelButton from "@/components/ExportExcelButton";
import { TFunction } from "i18next";
import ExportProgressModal from "@/components/ExportProgressModal";
import { dateFormat } from "@/utils/format";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { PurchaseOrderItem } from "@/types/purchase";
import PurchaseDetail from "@/pages/Purchase/pages/Order/components/PurchaseDetail";
import OrderDetailV2 from "../Order/components/OrderDetailV2";
import { numFormmater } from "@/pages/Requirement/util";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import { Requirement } from "@/types/requirement";
import RequirementDetail from "@/pages/Requirement/pages/Detail";
import { SaleOrderItemState } from "@/types/sales";
import ItemsOrderForm from "@/pages/Sales/components/ItemsOrderForm";
import { ItemType } from "antd/lib/menu/interface";
import useInspactionEnum from "@/pages/Warehouse/pages/Inspection/hooks/useInspactionEnum";
import useSourceType from "@/hooks/useSourceType";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import ColSetButton, { ColSetOption } from "@/components/ColSetButton";

enum TabEnum {
  IN = '1',
  OUT = '2',
}

interface PurchasedItemsFilters {
  keyword: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  arrivalStartTime: Dayjs | null;
  arrivalEndTime: Dayjs | null;
  createdStartAt: Dayjs | null;
  createdEndAt: Dayjs | null;
  orderType: ChangeEnum | undefined;
  confirmed: ConfirmedEnum | undefined;
}

const INITIAL_PURCHASED_ITEMS_FILTERS: PurchasedItemsFilters = {
  keyword: '',
  startTime: null,
  endTime: null,
  arrivalStartTime: null,
  arrivalEndTime: null,
  orderType: undefined,
  confirmed: undefined,
  createdEndAt: null,
  createdStartAt: null
};


/**
 * 采购来料明细：沿用原 InventoryDetailList 实现（itemList 接口，OrderItemV2 数据）
 */
const PurchasedItems: FC = ({ }) => {
  const { t } = useTranslation();
  const { getOpType, getChangeType, changeTypes } = useSourceType();
  const { getSpecialHandling } = useInspactionEnum();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [inventoryType, setInventoryType] = useState<TabEnum>(TabEnum.IN);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [openPur, setOpenPur] = useState<PurchaseOrderItem | null>(null);
  const [openRequirement, setOpenRequirement] = useState<Requirement | null>(null);
  const [openSale, setOpenSale] = useState<SaleOrderItemState | null>(null);
  const [openOrder, setOpenOrder] = useState<OrderV2 | null>(null);
  const [visibleCols, setVisibleCols] = useState<ColSetOption[] | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<PurchasedItemsFilters>(INITIAL_PURCHASED_ITEMS_FILTERS);
  const {
    keyword,
    startTime,
    endTime,
    arrivalStartTime,
    arrivalEndTime,
    createdEndAt,
    createdStartAt,
    orderType,
    confirmed,
  } = draftFilters;

  const [list, total, listLoading, { fetchList, clearData }] = useDataList<OrderItemV2, [CommonListParams<ItemListParams>, boolean]>({
    fetchApi: itemList
  });

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_PURCHASED_ITEMS_FILTERS);
    fetch(0, nextFilters);
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryType, replaceFilters])

  const fetch = (offset: number, filters: PurchasedItemsFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize || DEFAULT_PAGE_SIZE;

    let params: CommonListParams<ItemListParams> = {
      body: {
        keyword: filters.keyword,
        confirmState: filters.confirmed,
        orderTypes: filters.orderType !== undefined ? [filters.orderType] : undefined,
        arrivalAtStart: filters.arrivalStartTime?.startOf('day')?.valueOf(),
        arrivalAtEnd: filters.arrivalEndTime?.endOf('day')?.valueOf(),
        createdStartAt: filters.createdStartAt?.startOf('day')?.valueOf(),
        createdEndAt: filters.createdEndAt?.endOf('day')?.valueOf(),
      },
      startTime: filters.startTime?.startOf('day')?.valueOf(),
      endTime: filters.endTime?.endOf('day')?.valueOf(),
      pagination: { offset, take },
    }
    fetchList({
      ...params,
      controller
    }, true)
      .catch((err) => {
        if (err?.name !== "AbortError") {
          clearData();
          notification.error({ message: t('common.serverError') })
        }
      })
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_PURCHASED_ITEMS_FILTERS);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const isColHidden = (key: string) => visibleCols !== null && !visibleCols.some(item => item.key === key);

  const columns: ColumnsType<OrderItemV2> = useMemo(() => [
    {
      title: t('warehouse.refNo'),
      key: "source",
      hidden: isColHidden('source'),
      width: 180,
      fixed: "left",
      sorter: (a, b) => {
        const valA = getSourceNumber(a);
        const valB = getSourceNumber(b);
        return valA.localeCompare(valB);
      },
      render(_, record) {
        return <Typography.Text
          ellipsis={{ tooltip: getSourceNumber(record) }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => {
            record.sourceType === SourceTypeEnum.PURCHASE_ITEM && setOpenPur(record?.source || null)
            record.sourceType === SourceTypeEnum.REQUIREMENT_ITEM && setOpenRequirement(record?.source?.requirement || null)
            record.sourceType === SourceTypeEnum.SALE_ITEM && record?.source?.order && setOpenSale({
              ...record.source.order,
              items: [{ ...record.source }]
            })
          }}
        >
          {getSourceNumber(record)}
        </Typography.Text>
      }
    },
    {
      title: t('product.code'),
      key: "productCode",
      hidden: isColHidden('productCode'),
      width: 120,
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB, undefined, { numeric: true });
      },
      render(_, record) {
        return <SText text={record.product?.code ?? ''} />
      }
    },
    {
      title: t('product.name'),
      key: "productName",
      hidden: isColHidden('productName'),
      width: 150,
      render(_, record) {
        return <SText text={record.product?.name ?? ''} />
      }
    },
    {
      title: t('product.brand1'),
      key: "brand1",
      hidden: isColHidden('brand1'),
      width: 120,
      render(_, record) {
        return <SText text={record.product?.model ?? ''} />
      }
    },
    {
      title: t('purchase.brand'),
      key: "brand",
      hidden: isColHidden('brand'),
      width: 120,
      render(_, record) {
        return <SText text={record?.ext?.originalBrand ?? ''} />
      }
    },
    {
      title: t('purchase.mpn'),
      key: "mpn",
      hidden: isColHidden('mpn'),
      width: 120,
      render(_, record) {
        return <SText text={record?.ext?.originalCode ?? ''} />
      }
    },
    {
      title: t('product.uom'),
      key: "uom",
      hidden: isColHidden('uom'),
      width: 80,
      align: 'center',
      render(_, record) {
        return <SText text={record.product?.uom ?? ''} />
      }
    },
    {
      title: t('purchase.code'),
      key: "supplierCode",
      width: 80,
      align: 'center',
      hidden: isColHidden('supplierCode'),
      render: (_, item) => {
        let text = '';
        if (item.sourceType === SourceTypeEnum.PURCHASE_ITEM && item.source) {
          text = item.source.order?.supplier?.code ?? ''
        }
        return <SText text={text} style={{ width: "100%" }} />
      },
    },
    {
      title: t('purchase.name'),
      key: "supplierName",
      width: 80,
      align: 'center',
      hidden: isColHidden('supplierName'),
      render: (_, item) => {
        let text = '';
        if (item.sourceType === SourceTypeEnum.PURCHASE_ITEM && item.source) {
          text = item.source.order?.supplier?.name ?? ''
        }
        return <SText text={text} style={{ width: "100%" }} />
      },
    },
    {
      title: t("purchase.stPrice"),
      key: "price",
      width: 80,
      hidden: isColHidden('price'),
      render: (_, item) => {
        let text = '';
        if (item.sourceType === SourceTypeEnum.PURCHASE_ITEM && item.source) {
          text = numFormmater(Number(item.source.quotationRequest?.supplierPrice || item.source.price?.price || ''))
        }
        return <SText text={text} style={{ width: "100%" }} />
      },
    },
    {
      title: t('warehouse.batchNo'),
      key: "lot",
      hidden: isColHidden('lot'),
      width: 280,
      align: 'center',
      dataIndex: 'lot',
      render: (text) => <SText text={text || ''} style={{ width: "100%" }} />

    },
    {
      title: t('counting.quantity'),
      key: "count",
      hidden: isColHidden('count'),
      width: 90,
      align: 'center',
      dataIndex: 'count',
      sorter: (a, b) => {
        const valA = a?.count ?? 0;
        const valB = b?.count ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.count ? numFormmater(record.count) : '0'} />
      )
    },
    {
      title: t('warehouse.productOldCount'),
      key: 'productOldCount',
      hidden: isColHidden('productOldCount'),
      width: 90,
      render: (_, record) => (
        <SText text={record.productOldCount !== null ? numFormmater(record.productOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.productNewCount'),
      key: 'productNewCount',
      hidden: isColHidden('productNewCount'),
      width: 90,
      render: (_, record) => (
        <SText text={record.productNewCount !== null ? numFormmater(record.productNewCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemOldCount'),
      key: 'lotItemOldCount',
      hidden: isColHidden('lotItemOldCount'),
      width: 90,
      render: (_, record) => (
        <SText text={record.lotItemOldCount !== null ? numFormmater(record.lotItemOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemNewCount'),
      key: 'lotItemNewCount',
      hidden: isColHidden('lotItemNewCount'),
      width: 90,
      render: (_, record) => (
        <SText text={record.lotItemNewCount !== null ? numFormmater(record.lotItemNewCount) : ''} />
      )
    },
    {
      title: t('order.operationSource'),
      key: "opType",
      hidden: isColHidden('opType'),
      width: 120,
      dataIndex: "opType",
      render: (_, record) => {
        let info = getOpType(record.order.opType);
        return (
          info && <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationStatus'),
      key: "confirmed",
      hidden: isColHidden('confirmed'),
      width: 120,
      dataIndex: "confirmed",
      render: (_, record) => {
        // 根据tab显示不同的状态文字
        if ([ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.ORDER_IN, ChangeEnum.TEMP_IN].includes(record.order.type)) {
          // 入库：0-在途，1-已入库
          const label = record.order.confirmState === ConfirmedEnum.NOTCONFIRM ? (!!record.order.arrivalAt ? t('order.signed') : t('order.inTransit')) : t('order.inboundCompleted');
          const color = record.order.confirmState === ConfirmedEnum.NOTCONFIRM ? 'orange' : 'green';
          return <SText text={label} color={color} tagMode />;
        } else {
          // 出库：0-备料，1-已出库
          const label = record.order.confirmState === ConfirmedEnum.NOTCONFIRM ? t('order.preparing') : t('order.outboundCompleted');
          const color = record.order.confirmState === ConfirmedEnum.NOTCONFIRM ? 'orange' : 'green';
          return <SText text={label} color={color} tagMode />;
        }
      },
    },
    {
      title: t('order.oprtType'),
      key: "changeType",
      hidden: isColHidden('changeType'),
      width: 120,
      dataIndex: "changeType",
      render: (_, record) => {
        let info = getChangeType(record.order.type);
        return (
          info && <SText tagMode text={info?.label ?? ''} color={info?.color} />
        );
      },
    },
    {
      title: t('order.inOutNo'),
      key: "order",
      hidden: isColHidden('order'),
      width: 180,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a?.order?.number ?? "";
        const valB = b?.order?.number ?? "";
        return valA.localeCompare(valB);
      },
      render(_, record) {
        return <Typography.Text
          ellipsis={{ tooltip: record.order?.number ?? '' }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => setOpenOrder(record.order || null)}
        >
          {record.order?.number ?? ''}
        </Typography.Text>
      }
    },
    {
      title: t("product.materialProcess"),
      key: "process",
      hidden: isColHidden('process'),
      width: 120,
      sorter: (a, b) => {
        const valA = a?.product?.process ?? "";
        const valB = b?.product?.process ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={record?.product?.process ?? ''} />,
    },
    {
      title: t('order.preparationTime'),
      dataIndex: "createAt",
      key: "createAt",
      hidden: isColHidden('createAt'),
      width: 100,
      render: (_, record) => record?.order?.preTime && dateFormat(dayjs(record?.order?.createdAt)),
    },
    {
      title: t('warehouse.requiredArrivalAt'),
      dataIndex: "preTime",
      key: "preTime",
      hidden: isColHidden('preTime'),
      width: 100,
      render: (_, record) => record?.order?.preTime && dateFormat(dayjs(record?.order?.preTime)),
    },
    {
      title: t('warehouse.actualArrivalAt'),
      dataIndex: "arrivalAt",
      key: "arrivalAt",
      hidden: isColHidden('arrivalAt'),
      width: 100,
      render: (_, record) => record?.order?.arrivalAt && dateFormat(dayjs(record?.order?.arrivalAt)),
    },
    {
      title: t('order.confirmedTime'),
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      hidden: isColHidden('confirmedAt'),
      width: 100,
      render: (_, record) => record?.order?.confirmedAt && dateFormat(dayjs(record?.order?.confirmedAt)),
    },
    {
      title: t('purchase.remark'),
      dataIndex: "desc",
      key: "desc",
      hidden: isColHidden('desc'),
      width: 120,
      render: (_, record) => <SText text={record?.order?.desc ?? ""} />,
    },
    {
      title: t('warehouse.inputer'),
      dataIndex: "creator",
      key: "creator",
      hidden: isColHidden('creator'),
      width: 120,
      render: (_, record) => {
        if (record?.order?.creator) return (
          <Fragment>
            <WMSAvatar
              uniChar={record.order.creator.id}
              avatarId={record.order.creator.avatar}
              creator={record.order.creator}
              alt={record.order.creator ? getUserAvatarTip(record.order.creator) : ''}
              size={24}
            />
            <span>{getUserName(record.order.creator)}</span>
          </Fragment>
        )
        else return '-'
      },
    },
    {
      title: t('warehouse.confirmer'),
      dataIndex: "updater",
      key: "updater",
      hidden: isColHidden('updater'),
      width: 120,
      render: (_, record) => {
        if (record.order.confirmState === ConfirmedEnum.CONFIRM) {
          let user = record?.order?.confirmer || record?.order?.updater || null;
          if (user) return (
            <Fragment>
              <WMSAvatar
                uniChar={user.id}
                avatarId={user.avatar}
                creator={user}
                alt={getUserAvatarTip(user)}
                size={24}
              />
              <span>{getUserName(user)}</span>
            </Fragment>
          )
          else return '-'
        }
        else return '-'
      },
    },
  ], [t, visibleCols]);

  // 可配置列，key 与上方 columns 中各列的 key 一一对应
  const colSetOptions: ColSetOption[] = useMemo(() => [
    { key: 'source', label: t('warehouse.refNo') },
    { key: 'productCode', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'brand1', label: t('product.brand1') },
    { key: 'brand', label: t('purchase.brand') },
    { key: 'mpn', label: t('purchase.mpn') },
    { key: 'uom', label: t('product.uom') },
    { key: 'supplierCode', label: t('purchase.code') },
    { key: 'supplierName', label: t('purchase.name') },
    { key: 'price', label: t('purchase.stPrice') },
    { key: 'lot', label: t('warehouse.batchNo') },
    { key: 'count', label: t('counting.quantity') },
    { key: 'productOldCount', label: t('warehouse.productOldCount') },
    { key: 'productNewCount', label: t('warehouse.productNewCount') },
    { key: 'lotItemOldCount', label: t('warehouse.lotItemOldCount') },
    { key: 'lotItemNewCount', label: t('warehouse.lotItemNewCount') },
    { key: 'opType', label: t('order.operationSource') },
    { key: 'confirmed', label: t('order.operationStatus') },
    { key: 'changeType', label: t('order.oprtType') },
    { key: 'order', label: t('order.inOutNo') },
    { key: 'process', label: t('product.materialProcess') },
    { key: 'createAt', label: t('order.preparationTime') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'confirmedAt', label: t('order.confirmedTime') },
    { key: 'desc', label: t('purchase.remark') },
    { key: 'creator', label: t('warehouse.inputer') },
    { key: 'updater', label: t('warehouse.confirmer') },
  ], [t]);


  const itemColumns = useMemo(() => [
    { key: 'number', label: t('warehouse.refNo') },
    { key: 'code', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'brand1', label: t('product.brand1') },
    { key: 'brand', label: t('purchase.brand') },
    { key: 'mpn', label: t('purchase.mpn') },
    { key: 'uom', label: t('product.uom') },
    { key: 'supplierCode', label: t('purchase.code') },
    { key: 'supplierName', label: t('purchase.name') },
    { key: 'price', label: t('purchase.stPrice') },
    { key: 'lot', label: t('warehouse.batchNo') },
    { key: 'count', label: t('counting.quantity') },
    { key: 'productOldCount', label: t('warehouse.productOldCount') },
    { key: 'productNewCount', label: t('warehouse.productNewCount') },
    { key: 'lotItemOldCount', label: t('warehouse.lotItemOldCount') },
    { key: 'lotItemNewCount', label: t('warehouse.lotItemNewCount') },
    { key: 'specialHandlingAggregate', label: t('inspection.specialHandlingAggregate') },

    { key: 'confirmed', label: t('order.operationStatus') },
    { key: 'changeType', label: t('order.oprtType') },
    { key: 'order', label: t('order.inOutNo') },
    { key: 'process', label: t('product.materialProcess') },
    { key: 'createAt', label: t('order.preparationTime') },
    { key: 'creator', label: t('warehouse.inputer') },
    { key: 'updater', label: t('warehouse.confirmer') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'confirmedAt', label: t('order.confirmedTime') },
    { key: 'desc', label: t('purchase.remark') },
    { key: 'finalPurchaseQuantity', label: t('requirement.finalPurQuantity') },
  ], [t]);

  const itemDataMapper = (item: OrderItemV2, t: TFunction) => {
    const finalPurchaseQuantity =
      item.sourceType === SourceTypeEnum.PURCHASE_ITEM
        ? Number(item.source?.count ?? '')
        : item.sourceType === SourceTypeEnum.REQUIREMENT_ITEM
          ? Number(item.source?.puredCount ?? '')
          : NaN;

    // 确认状态显示不同的状态文字
    let oprtType = getChangeType(item.order.type);
    let operationSource = getOpType(item.order.opType);
    let inboundStatus = item.order.confirmState === ConfirmedEnum.NOTCONFIRM ? (!!item.order.arrivalAt ? t('order.signed') : t('order.inTransit')) : t('order.inboundCompleted');
    let outboundStatus = item.order.confirmState === ConfirmedEnum.NOTCONFIRM ? t('order.preparing') : t('order.outboundCompleted');
    let isInbound = [ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.ORDER_IN, ChangeEnum.TEMP_IN].includes(item.order.type)

    return {
      [t('warehouse.refNo')]: getSourceNumber(item),
      [t('product.code')]: item.product.code || '',
      [t('product.name')]: item.product.name || '',
      [t('product.brand1')]: item.product.model || '',
      [t('purchase.brand')]: item?.ext?.originalBrand ?? '',
      [t('purchase.mpn')]: item?.ext?.originalCode ?? '',
      [t('product.uom')]: item.product.uom || '',
      [t('purchase.code')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item.source?.order?.supplier?.code ?? '' : '',
      [t('purchase.name')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM ? item.source?.order?.supplier?.name ?? '' : '',
      [t('purchase.stPrice')]: item.sourceType === SourceTypeEnum.PURCHASE_ITEM
        ? numFormmater(Number(item.source?.quotationRequest?.supplierPrice || item.source?.price?.price || ''))
        : '',
      [t('warehouse.batchNo')]: item.lot || '',
      [t('counting.quantity')]: item.count ? numFormmater(item.count) : '',
      [t('warehouse.productOldCount')]: item.productOldCount !== null ? numFormmater(item.productOldCount) : '',
      [t('warehouse.productNewCount')]: item.productNewCount !== null ? numFormmater(item.productNewCount) : '',
      [t('warehouse.lotItemOldCount')]: item.lotItemOldCount !== null ? numFormmater(item.lotItemOldCount) : '',
      [t('warehouse.lotItemNewCount')]: item.lotItemNewCount !== null ? numFormmater(item.lotItemNewCount) : '',
      [t('inspection.specialHandlingAggregate')]: getSpecialHandling(item.specialHandlingAggregate)?.label ?? '',
      [t('order.operationSource')]: operationSource?.label ?? '',
      [t('order.operationStatus')]: isInbound ? inboundStatus : outboundStatus,
      [t('order.oprtType')]: oprtType?.label ?? "",
      [t('order.inOutNo')]: item?.order?.number || '',
      [t('product.materialProcess')]: item?.product?.process ?? '',
      [t('order.preparationTime')]: item?.order?.createdAt && dateFormat(dayjs(item?.order?.createdAt)),
      [t('warehouse.inputer')]: getUserName(item?.order?.creator) || '',
      [t('warehouse.confirmer')]: item?.order?.confirmState === ConfirmedEnum.CONFIRM ? getUserName(item?.order?.updater || item?.order?.confirmer) : '',
      [t('warehouse.requiredArrivalAt')]: item?.order?.preTime && dateFormat(dayjs(item?.order?.preTime)),
      [t('warehouse.actualArrivalAt')]: item?.order?.arrivalAt && dateFormat(dayjs(item?.order?.arrivalAt)),
      [t('order.confirmedTime')]: item?.order?.confirmedAt && dateFormat(dayjs(item?.order?.confirmedAt)),
      [t('purchase.remark')]: item?.order?.desc ?? "",
      [t('requirement.finalPurQuantity')]:
        Number.isNaN(finalPurchaseQuantity)
          ? ''
          : numFormmater(finalPurchaseQuantity),
    };
  };

  const handleExportAll = async (): Promise<OrderItemV2[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allOrderData: OrderItemV2[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        let params: CommonListParams<ItemListParams> = {
          body: {
            keyword: appliedFilters.keyword,
            confirmState: appliedFilters.confirmed,
            orderTypes: appliedFilters.orderType !== undefined ? [appliedFilters.orderType] : undefined,
            arrivalAtStart: appliedFilters.arrivalStartTime?.startOf('day')?.valueOf(),
            arrivalAtEnd: appliedFilters.arrivalEndTime?.endOf('day')?.valueOf(),
            createdStartAt: appliedFilters.createdStartAt?.startOf('day')?.valueOf(),
            createdEndAt: appliedFilters.createdEndAt?.endOf('day')?.valueOf()
          },
          startTime: appliedFilters.startTime?.startOf('day')?.valueOf(),
          endTime: appliedFilters.endTime?.endOf('day')?.valueOf(),
          pagination: { offset, take: batchSize },
        }
        const response = await itemList(params, true);

        if (response.body && response.body.length > 0) {
          allOrderData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allOrderData.length /
                (response.pagination?.size || allOrderData.length)) *
                100
            ),
            99
          );
          setExportProgress(progress);
        } else {
          hasMore = false;
        }

        if ((response?.body?.length || 0) < batchSize) {
          hasMore = false;
        }
      }
      setExportProgress(100);
      return allOrderData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const getExportItems = useCallback(() => {
    let resItems: ItemType[] = [];
    resItems.push(
      {
        key: 1,
        label: (
          <ExportExcelButton
            list={list}
            columns={itemColumns}
            defaultSelectedColumns={[
              t('warehouse.refNo'),
              t('product.code'),
              t('order.oprtType'),
              t('product.brand1'),
              t('warehouse.productNum')
            ]}
            dataMapper={itemDataMapper}
            fileName={`${t('layout.inOutDetail')}.xlsx`}
            type="link"
            style={{ color: "black" }}
          />
        )
      },
      {
        key: 2,
        label: (
          <ExportExcelButton
            isAll
            loadData={handleExportAll}
            columns={itemColumns}
            defaultSelectedColumns={[
              t('warehouse.refNo'),
              t('product.code'),
              t('order.oprtType'),
              t('product.brand1'),
              t('warehouse.productNum')
            ]}
            dataMapper={itemDataMapper}
            fileName={`${t('layout.purInvItem')}.xlsx`}
            type="link"
            style={{ color: "black" }}
          />
        )
      }
    )
    return resItems;
  }, [t, handleExportAll, itemDataMapper, itemColumns, list])

  const headerChildren = (
    <Flex justify='space-between' style={{ width: "100%" }} align='center' gap={6}>
      <Flex gap={4} align='center'>
        <ColSetButton
          options={colSetOptions}
          value={visibleCols ?? colSetOptions}
          onOk={setVisibleCols}
        />
        <Dropdown menu={{ items: getExportItems() }}>
          <Button onClick={(e) => e.preventDefault()}>
            {t('common.export')}
          </Button>
        </Dropdown>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
        // disabled={listLoading}
        >
          {({ submit }) => (
            <>
              <Tooltip title={t('common.createTime')}>
                <DatePicker.RangePicker
                  value={[createdStartAt, createdEndAt]}
                  onChange={value => updateDraftFilters({
                    createdStartAt: value?.[0] ?? null,
                    createdEndAt: value?.[1] ?? null,
                  })}
                  style={{ width: 240 }}
                />
              </Tooltip>
              <Tooltip title={t('warehouse.signTime')}>
                <DatePicker.RangePicker
                  value={[arrivalStartTime, arrivalEndTime]}
                  onChange={value => updateDraftFilters({
                    arrivalStartTime: value?.[0] ?? null,
                    arrivalEndTime: value?.[1] ?? null,
                  })}
                  placeholder={[t('order.startTime'), t('order.endTime')]}
                  style={{ width: 240 }}
                />
              </Tooltip>
              <Tooltip title={t('order.confirmedTime')}>
                <DatePicker.RangePicker
                  value={[startTime, endTime]}
                  onChange={value => updateDraftFilters({
                    startTime: value?.[0] ?? null,
                    endTime: value?.[1] ?? null,
                  })}
                  placeholder={[t('order.startTime'), t('order.endTime')]}
                  style={{ width: 240 }}
                />
              </Tooltip>
              <Select style={{ minWidth: 120 }} value={confirmed} placeholder={t('warehouse.plconfed')}
                options={[
                  { label: t('warehouse.noconf'), value: ConfirmedEnum.NOTCONFIRM },
                  { label: t('warehouse.confed'), value: ConfirmedEnum.CONFIRM },
                ]}
                onChange={value => updateDraftFilters({ confirmed: value })} allowClear
              />
              <Select style={{ minWidth: 160 }} value={orderType}
                options={changeTypes}
                onChange={value => updateDraftFilters({ orderType: value })}
                placeholder={t('order.selectChangeTypePl')}
                allowClear
              />
              <Input
                style={{ width: 180 }}
                placeholder={t('warehouse.searchMultiCodePlaceholder')}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%", marginBottom: 10 }}>
      <STableLayout
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => { fetch(offset, appliedFilters); }}
        columns={columns as ColumnsType<AnyObject>}
        dataSource={list}
        loading={listLoading}
        scrollX={1200}
        headerChildren={headerChildren}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {
        openPur && <PurchaseDetail
          canEdit={false}
          order={openPur}
          key={openPur.id}
          handleChangeStatus={() => { }}
          handleOk={() => Promise.resolve()}
          handleCancel={() => setOpenPur(null)}
          open={!!openPur}
          confirmLoading={false}
          orderItemId={openPur.id}
        />
      }
      {
        openRequirement && <RequirementDetail
          id={openRequirement.id}
          handleOk={() => { setOpenRequirement(null) }}
          number={openRequirement.number}
          handleCancel={() => { }}
          onCancel={() => setOpenRequirement(null)}
        />
      }
      {
        openOrder && <OrderDetailV2 readonly
          handleRm={() => { }}
          id={openOrder.id}
          open={true}
          handleCancel={() => setOpenOrder(null)}
        />
      }
      {
        openSale && <ItemsOrderForm editMode="READ" onFormSuccess={() => { }}
          leftTitle={<SText text={t('requirement.saleOrderNumber') + openSale.number} />}
          onClose={() => setOpenSale(null)}
          fetchId={openSale.id}
        />
      }
    </Flex>
  )
};

export default PurchasedItems;
